import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import styled from 'styled-components';
import { Panel } from '@rtkwlf/ui';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { apiRequest } from '../../../../APIClient';
import { APIError } from '../../../../APIError';
import { ONBOARDING_ACTIONS } from '../../../../actions/onboarding';
import { PageTitle } from '../../../../widgets/PageTitle';
import Viewport from '../../../../widgets/Viewport';
import ViewHeader from '../../../../widgets/ViewHeader';
import { ContactValidationSchema } from '../../validationSchemas';
import {
  cleanPermissions,
  ContactFormValues,
  getFormValues,
} from './constants';
import { OnboardingContactForm } from './Form';
import {
  getPrimaryContactId,
  onboardingSelector,
} from '../../../../reducers/onboarding';
import ErrorJumbotron from '../../../../widgets/ErrorJumbotron';
import LoadingIndicator from '../../../../widgets/LoadingIndicator';
import { HTTP_STATUS, ROUTE_PATH } from '../../../../constants';
import { OBContact } from '../../../../types';
import { chainApiDispatches } from '../../../../utils';
import { ScrollSectionKeys } from '../../site-config/OnboardingSiteConfig';

const NOT_FOUND = new APIError('Contact not found', HTTP_STATUS.NOT_FOUND);
const NOT_EDITABLE = new APIError(
  'Contact not editable',
  HTTP_STATUS.BAD_REQUEST,
);

export const EditView: React.FunctionComponent = () => {
  const { uuid } = useParams<{ uuid: string }>();
  // this view should only be presented when the contact is found
  const contact = useSelector(onboardingSelector.getContactByUuid(uuid))!;
  const contacts = useSelector(onboardingSelector.getContacts);

  const initialValues = React.useMemo(() => getFormValues(contact), [contact]);

  const dispatch = useDispatch();

  const onSubmit = React.useCallback(
    (values: ContactFormValues) => {
      const contact = cleanPermissions(values);
      const primaryContactId = getPrimaryContactId(contacts);
      if (
        contact.contactType === 'primary' &&
        primaryContactId &&
        uuid !== primaryContactId
      ) {
        // Reassign previous primary contact to normal because we only allow one primary contact
        const changedPrimary: OBContact = {
          ...contacts[primaryContactId],
          contactType: 'normal',
        };
        dispatch(
          ONBOARDING_ACTIONS.SET_CONTACT(primaryContactId, changedPrimary),
        );
        dispatch(ONBOARDING_ACTIONS.SET_CONTACT(uuid, contact));
        chainApiDispatches(dispatch, [
          apiRequest.updateOBContact(primaryContactId, changedPrimary),
          apiRequest.updateOBContact(uuid, contact),
        ]);
      } else {
        dispatch(ONBOARDING_ACTIONS.SET_CONTACT(uuid, contact));
        dispatch(apiRequest.updateOBContact(uuid, contact));
      }
    },
    [dispatch, uuid, contacts],
  );

  return (
    <Formik<ContactFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={ContactValidationSchema}
    >
      <OnboardingContactForm view={ScrollSectionKeys.CONTACTS} />
    </Formik>
  );
};

const ErrorPanel = styled(Panel)`
  margin-left: 0;
  margin-right: 0;
  min-height: 225px;
`;

/**
 * This view should guard against scenarios where the contact is not editable
 *  e.g. still loading, not found, not allowed (MSP workflow)
 *  and render an error banner instead of the form
 */
export const OnboardingContactEditView: React.FunctionComponent = () => {
  const isLoading = useSelector(onboardingSelector.getContactSectionLoading);

  const { uuid } = useParams<{ uuid: string }>();
  const contact = useSelector(onboardingSelector.getContactByUuid(uuid));

  let errorContent: React.ReactNode;

  if (isLoading) {
    errorContent = (
      <LoadingIndicator size="lg" centered>
        Loading contact...
      </LoadingIndicator>
    );
  } else if (!contact) {
    errorContent = <ErrorJumbotron error={NOT_FOUND} />;
  } else if (contact.isMSP) {
    errorContent = <ErrorJumbotron error={NOT_EDITABLE} />;
  }

  if (errorContent) {
    return (
      <Viewport>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>Edit Contact</BreadcrumbItem>
        </Breadcrumb>
        <PageTitle title="Edit Contact" />
        <ViewHeader title="Edit Contact" />

        <ErrorPanel className="min-height-for-loading-indicator">
          {errorContent}
        </ErrorPanel>
      </Viewport>
    );
  }

  const content = errorContent ? (
    <ErrorPanel className="min-height-for-loading-indicator">
      {errorContent}
    </ErrorPanel>
  ) : (
    <EditView />
  );

  return (
    <Viewport>
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Edit Contact</BreadcrumbItem>
      </Breadcrumb>
      <PageTitle title="Edit Contact" />
      <ViewHeader title="Edit Contact" />
      {content}
    </Viewport>
  );
};
